import { createStyles, makeStyles, Theme, focusStyle, interactiveLinkStyle } from 'ui-components';
import { fontText } from 'common-react';

const tranparentButton = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
}

interface FundDetailsButtonWithDialogStyleProps {
  titleLength?: number;
}

export default makeStyles<Theme, FundDetailsButtonWithDialogStyleProps>((theme: Theme) => createStyles({
  fundDetailsDialog: {
    maxWidth: '100%',
    width: '100%',
    maxHeight: '100%',
    top: 0,
    margin: '0',
    backgroundColor: theme.color.greys[10],
  },
  fundDetailsDialogWrapper: {
    [theme.breakpoints.down('sm')]: {
      padding: '0 16px',
    },
  },
  fundDetailsDialogPaperWrapper: {
    justifyContent: 'flex-end',
  },
  dialogContent: {
    padding: 0,
    position: 'relative',
    overflowX: 'hidden',
    '&:first-of-type': {
      padding: 0,
      backgroundColor: theme.color.greys[10],
    }
  },
  leftColumn: {
    padding: '40px 32px',
    background: theme.color.greys[0],
    [theme.breakpoints.down('sm')]: {
      padding: '24px 16px',
    },
  },
  rightColumn: {
    padding: '40px 32px',
    background: theme.color.greys[20],
    [theme.breakpoints.down('sm')]: {
      padding: '20px 16px 100px 16px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 'none',
    },
  },
  closeButton: {
    marginRight: 16,
    ...tranparentButton,
  },
  closeButtonWrapper: {
    position: 'absolute',
    padding: 0,
    top: 24,
    right: 24,
    width: 24,
    height: 24,
  },
  fundName: {
    '&.MuiTypography-root.MuiTypography-h2': {
      font: fontText('normal', theme.fontSize.xxxl, 48),
      [theme.breakpoints.down('sm')]: {
        font: fontText('normal', theme.fontSize.xl, 32),
      }
    }
  },
  goalsWrapper: {
    padding: '32px 0 24px 0',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 0,
    },
  },
  goalsTitle: {
    font: fontText('normal', theme.fontSize.m, 32),
    marginBottom: 20,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 16,
    },
  },
  listItem: {
    font: fontText('normal', theme.fontSize.s, 18),
    color: theme.color.greys[60],
    padding: 0,
    alignItems: 'flex-start',
    '&:before': {
      content: '"\\2022"',
      color: theme.color.greys[60],
      display: 'inline-block',
      paddingRight: 16,
    },
  },
  ratingWrapper: {
    marginTop: 16,
    textTransform: 'uppercase',
  },
  ratingStars: {
    marginTop: 10,
  },
  ratingIcon: {
    width: 16,
    height: 16,
    marginRight: 5,
  },
  documentsWrapper: {
    marginTop: 32,
    marginBottom: 12,
  },
  documentsListWrapper: {
    padding: 0,
    marginTop: 12,
    marginBottom: 40,
  },
  documentsTitle: {
    font: fontText('normal', theme.fontSize.m, 24),
  },
  docListItem: {
    padding: 0,
    marginBottom: 8,
    alignItems: 'center',
  },
  docListItemIcon: {
    marginRight: 10,
  },
  docListItemText: {
    margin: 0,
    '& a': {
      font: fontText('normal', theme.fontSize.s, 18),
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    '& a:active': {
      backgroundColor: 'transparent',
    }

  },
  costsWrapper: {
    marginBottom: 24,
  },
  costsTitle: {
    font: fontText('normal', theme.fontSize.xl, 32),

  },
  costItem: {
    marginBottom: 16,
  },
  costItemInfoTitle: {
    font: fontText('normal', theme.fontSize.s, 16),
    textTransform: 'uppercase',
    color: theme.color.greys[60],
  },
  costItemInfoValue: {
    font: fontText('normal', theme.fontSize.s, 18),
    color: theme.color.greys[80],
    textAlign: 'left',
    marginTop: 8,
  },
  costInfo: {
    font: fontText('normal', theme.fontSize.s, 18),
    color: theme.color.greys[60],
    marginTop: 16,
    marginBottom: 24,
  },
  additionalTab: {
    maxWidth: theme.space.fundDetailsMaxWidthDefault,
    margin: '0 auto',
    padding: '16px 20px',
    backgroundColor: theme.color.violets[150],
    color: theme.color.constants.greys[0],
    font: fontText('normal', theme.fontSize.base, 24),
    borderRadius: 12,
    [theme.breakpoints.down('sm')]: {
      padding: 16,
    },
  },
  fundBigChartCard: {
    maxWidth: theme.space.fundDetailsMaxWidthDefault,
    margin: ({ titleLength }) => (() => {
      if (titleLength && titleLength > 100) {
        return '-178px auto 16px auto';
      }
      return '-218px auto 16px auto';
    })(),
    [theme.breakpoints.down('sm')]: {
      margin: ({ titleLength }) => (() => {
        if (titleLength && titleLength > 100) {
          return '-15px auto 16px auto';
        }
        return '-32px auto 16px auto';
      })(),
      padding: '24px 16px'
    },
  },
  fundBigChartWrapper: {
    paddingTop: 24,
    [theme.breakpoints.down('sm')]: {
      marginTop: 24,
      borderTop: `1px solid ${theme.color.greys[15]}`,
    },
  },
  bottomBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0 auto',
    maxWidth: theme.space.bottomBarMaxWidth,
    padding: '0 67px 0 67px',
    [theme.breakpoints.down(theme.breakpoints.values.lg)]: {
      padding: '0 56px 0 42px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0 0 16px',
      alignItems: 'center'
    },
  },
  bottomBarFundName: {
    color: theme.color.greys[80],
    [theme.breakpoints.down('sm')]: {
      paddingRight: 4,
    },
  },
  fundDetailsDialogGrid: {
    paddingBottom: 125,
    maxWidth: theme.space.fundDetailsMaxWidthDefault,
  },
  customButton: {
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'transparent',
      border: 'none',
      width: '100%',
      margin: 0,
      padding: 0,
      textAlign: 'left',

      '&:hover, &:focus': {
        border: 'none',
        outline: 'none',
      },

      '&:focus-visible': {
        ...focusStyle(theme),
      },
    },
  },
  titleAsButton: {
    backgroundColor: 'transparent',
    border: 'none',
    margin: 0,
    padding: 0,
    textAlign: 'left',
    cursor: 'pointer',

    '&:hover, &:focus': {
      border: 'none',
      outline: 'none',
    },

    '&:focus-visible': {
      ...focusStyle(theme),
    },

    '& svg path': {
      fill: theme.color.greys[80],
    },
  },
  bottomBarPreviousName: {
    color: theme.color.greys[60],
    textTransform: 'capitalize',
    marginTop: 4,
  },
  bottomBarButton: {
    minWidth: 0,
  },
}), {name: 'FundDetailsButtonWithDialog'});
